/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppDispatch } from 'helpers/store'
import { closeSecondaryModal } from 'helpers/actions'

import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import CompanyAutocomplete from 'components/CompanyAutocomplete'
import { Company } from 'types/entities/Company'

interface ChangeCompanyModalProps {
    title?: string
    subtitle?: string
    initialCompany?: Company | null
    onSave: (company: Company) => Promise<void> | void
    className?: string
}

const ChangeCompanyModal: React.FC<ChangeCompanyModalProps> = ({ title, subtitle, initialCompany, onSave, className }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(initialCompany ?? null)
    const [isSaving, setIsSaving] = useState(false)

    const handleSave = async (shouldClose: boolean = false) => {
        if (!selectedCompany) return

        setIsSaving(true)
        try {
            await onSave(selectedCompany)
            if (shouldClose) {
                dispatch(closeSecondaryModal())
            }
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <UiFormModalShell
            className={className ? `ChangeCompanyModal ${className}` : 'ChangeCompanyModal'}
            title={title ?? t('Change Company')}
            subtitle={subtitle ?? t('Select a new company')}
            showHeader={true}
            footer={
                <UiFormActionsBar
                    className="w-full px-6 py-4"
                    primaryText={t('Save') as string}
                    onPrimary={() => handleSave(false)}
                    saveAndCloseText={t('Save & Close') as string}
                    onSaveAndClose={() => handleSave(true)}
                    loading={isSaving}
                    disabled={!selectedCompany || selectedCompany.id === initialCompany?.id}
                />
            }
        >
            <UiFormInner>
                <CompanyAutocomplete
                    label={t('Company') as string}
                    name="company_id"
                    placeholder={t('Search for company...') as string}
                    onSelect={setSelectedCompany}
                    defaultValue={selectedCompany}
                />
            </UiFormInner>
        </UiFormModalShell>
    )
}

export default ChangeCompanyModal
